import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link as RouterLink } from 'react-router-dom';
import { slugify } from '../utils/slugify';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TableSortLabel } from '@mui/material';import { Helmet } from 'react-helmet-async';

// Pomocnicza funkcja do formatowania daty, identyczna jak w WcPositionMatrix.js
const convertUTCToLocal = (utcValue) => {
  if (!utcValue) return '';
  let utcDate = new Date(utcValue.replace(' ', 'T') + 'Z');
  return utcDate.toLocaleString('pl-PL', { timeZone: 'Europe/Warsaw' });
};

// Oryginalny gradient i cieniowanie z pliku wzorcowego
const getGradientColor = (value) => {
  if (value === 0) return 'rgba(255, 255, 255, 0.3)'; // Pojaśniony neutralny szary
  const scaledValue = Math.max(0, value / 0.8);
  const grayBase = 255;
  const r = Math.round(grayBase * (1 - scaledValue));
  const g = Math.round(grayBase * (1 - scaledValue) + 255 * scaledValue);
  const b = Math.round(grayBase * (1 - scaledValue) + 50 * scaledValue); // Lekkie niebieskie dla głębi
  const alpha = 0.3 + scaledValue * 0.7;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const formatChance = (chance, useTilde = false) => {
  if (typeof chance !== 'number' || isNaN(chance)) return '—';

  const percent = chance * 100;

  if (percent < 0.001) return useTilde ? '~0.0%' : '0.0%';
  if (percent > 99.999) return useTilde ? '~100%' : '100.0%';

  const decimals = ((percent < 0.1 && percent > 0) || (percent > 99.9 && percent < 100)) ? 2 : 1;
  const formatted = percent.toFixed(decimals);

  return useTilde && (percent < 0.01 || percent > 99.99)
    ? `~${formatted}%`
    : `${formatted}%`;
};

const WcSummary = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Stany obsługujące dynamiczne sortowanie kolumn
  const [orderBy, setOrderBy] = useState('pctWinner'); // Domyślnie sortujemy po szansie na zwycięzcę
  const [order, setOrder] = useState('desc');          // Domyślny kierunek malejący

  // Pobranie danych z dedykowanego API dla etapów pucharowych
  useEffect(() => {
    setLoading(true);
    axios.get(`${process.env.REACT_APP_API_URL}/api/simulation/worldcup/latest-stages`)
      .then(response => {
        setData(response.data);
        setLoading(false);
      })
      .catch(err => {
        console.error("Błąd ładowania podsumowania etapów MŚ:", err);
        setError("Nie udało się pobrać danych symulacji.");
        setLoading(false);
      });
  }, []);

  // Funkcja zmieniająca kryteria sortowania po kliknięciu nagłówka
  const handleRequestSort = (property) => {
  // Jeśli klikamy w tę samą kolumnę, odwracamy kierunek.
  // Jeśli klikamy w NOWĄ kolumnę, wymuszamy domyślnie 'desc' (malejąco).
  const isDesc = orderBy === property && order === 'desc';
  setOrder(isDesc ? 'asc' : 'desc');
  setOrderBy(property);
};

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6">Ładowanie podsumowania fazy pucharowej MŚ...</Typography>
      </Box>
    );
  }

  if (error || !data || !data.results) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error">{error || "Brak danych"}</Typography>
      </Box>
    );
  }

  // Logika sortowania tablicy wyników w locie przed renderowaniem
  const sortedResults = [...data.results].sort((a, b) => {
    const valueA = a[orderBy] ?? 0;
    const valueB = b[orderBy] ?? 0;

    if (valueB < valueA) {
      return order === 'desc' ? -1 : 1;
    }
    if (valueB > valueA) {
      return order === 'desc' ? 1 : -1;
    }
    return 0;
  });
// Konfiguracja kolumn pucharowych z kluczami odpowiadającymi polom w JSON
  const columns = [
    { id: 'pct1_16', label: '1/16 Finału' },
    { id: 'pct1_8', label: '1/8 Finału' },
    { id: 'pct1_4', label: '1/4 Finału' },
    { id: 'pct1_2', label: '1/2 Finału' },
    { id: 'pctFinal', label: 'Finał' },
    { id: 'pctWinner', label: 'Zwycięzca' }
  ];

  return (
    <Box sx={{ width: '100%', p: { xs: 1, sm: 2 } }}>
      <Helmet>
        <title>Podsumowanie szans - Mistrzostwa Świata | Symuliga</title>
      </Helmet>

      {/* Tytuł nagłówka */}
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
          Mistrzostwa Świata - Szanse w fazie pucharowej
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Data symulacji: {convertUTCToLocal(data.simulationDateTime)}
        </Typography>
      </Box>

      {/* Kontener tabeli (maksymalnie 1000px, wyśrodkowany, ze wszystkimi stylami graficznymi) */}
      <Box sx={{ maxWidth: '1000px', margin: '0 auto' }}>
        <TableContainer
          component={Paper}
          sx={{
            marginBottom: 1,
            margin: '0 auto',
            width: 'auto',
            maxWidth: 'fit-content',
            overflowX: 'auto',
            position: 'relative',
            borderRadius: '12px',
            WebkitOverflowScrolling: 'touch',
            '&::-webkit-scrollbar': { width: '8px', height: '8px' },
            '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.3)', borderRadius: '4px' },
            '&::before': {
              content: '"SymuLiga.pl"',
              position: 'absolute',
              top: '60%',
              left: '50%',
              transform: 'translate(-50%, -50%) rotate(-45deg)',
              fontSize: { xs: '3rem', sm: '4rem', md: '5rem' },
              fontWeight: 'bold',
              color: 'rgba(0, 0, 0, 0.15)',
              pointerEvents: 'none',
              zIndex: 0,
              whiteSpace: 'nowrap',
            },
          }}
        >
          <Table size="small" sx={{ width: 'auto', tableLayout: 'auto', borderCollapse: 'collapse' }} aria-label="world cup knockout stages probability table">
            <TableHead>
              <TableRow>
                {/* Niesortowalna kolumna z nazwą drużyny */}
                <TableCell
                  sx={{
                    position: 'sticky',
					textAlign: 'right',
                    left: 0,
                    top: 0,
                    zIndex: 3,
                    backgroundColor: 'background.paper',
                    width: 'auto',
                    maxWidth: '100%',
                    whiteSpace: 'normal',
                    padding: '6px',
                    fontSize: { xs: '0.8rem', sm: '0.9rem' },
                    borderRight: '1px solid rgba(224, 224, 224, 1)',
                  }}
                >
                  Drużyna
                </TableCell>
                
                {/* Dynamiczne i sortowalne nagłówki etapów pucharowych */}
                {columns.map((col) => (
                  <TableCell
                    key={col.id}
                    align="center"
                    sortDirection={orderBy === col.id ? order : false}
                    sx={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                      backgroundColor: 'background.paper',
                      width: 'auto',
                      padding: '6px',
                      fontSize: { xs: '0.8rem', sm: '0.9rem' },
                      borderBottom: '2px solid rgba(224, 224, 224, 1)',
                      fontWeight: 'bold'
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === col.id}
                      direction={orderBy === col.id ? order : 'desc'}
                      onClick={() => handleRequestSort(col.id)}
                    >
                      {col.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
				
			
              {sortedResults.map((row, rowIndex) => {
				  const stageProbabilities = [
							  row.pct1_16,
							  row.pct1_8,
							  row.pct1_4,
							  row.pct1_2,
							  row.pctFinal,
							  row.pctWinner
							];
                return (
                  <TableRow key={rowIndex}>
                    {/* Nazwa drużyny jako link */}
                    <TableCell
                      
                      sx={{
							  position: 'sticky',
							  textAlign: 'right',
							  left: 0,
							  zIndex: 3,
							  backgroundColor: 'background.paper',
							  width: 'auto', // Dopasowanie do zawartości
							  minWidth: 40, // Minimalna szerokość dostosowana
							  padding: '5px',
							  fontSize: { xs: '0.8rem', sm: '0.9rem' },
							  borderRight: '1px solid rgba(224, 224, 224, 1)',
							}}
                    >
                      {row.teamName}
                    </TableCell>
					
                    {/* Komórki szans z oryginalnymi paddingami, cieniem i efektem powiększenia przy hoverze */}
                    {stageProbabilities.map((prob, colIndex) => (
                      <TableCell
                        key={colIndex}
                        align="center"
                        sx={{
								width: 'auto', // Proporcjonalne dostosowanie
								minWidth: 70, // Minimalna szerokość dostosowana
								backgroundColor: getGradientColor(prob),
								color: 'black', // Zawsze czarny dla awansu
								fontWeight: 'bold',
								fontSize: { xs: '0.8rem', sm: '0.9rem' },
								padding: '5px',
								textShadow: '0.5px 0.5px 1px rgba(0, 0, 0, 0.3)',
								transition: 'background-color 0.3s',
								'@media (hover: hover)': {
								  '&:hover': {
									backgroundColor: getGradientColor(Math.min(prob + 0.1, 1)),
									transform: 'scale(1.05)',
								  },
								},
							  }}
                      >
						{formatChance(prob, false)}                      
					</TableCell>
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default WcSummary;